export type Currency = "GEL" | "USD" | "EUR" | "GBP" | "TRY" | "RUB" | "JPY" | "KWD" | "UAH" | "VND" | "PLN" | "CHF";
export const CURRENCIES: Currency[] = ["GEL", "USD", "EUR", "GBP", "TRY", "RUB", "JPY", "KWD", "UAH", "VND", "PLN", "CHF"];

export interface Money {
  minor: number;
  currency: Currency;
}

export interface LedgerAccount {
  id: string;
  bookId: string;
  name: string;
  currency: Currency;
  // The institution's identifier (IBAN, account number) and the short label
  // that tells accounts apart; displayLabel is derived by the server.
  reference?: string;
  displayValue?: string;
  displayLabel?: string;
  archived?: boolean;
}

// What an institution reported and the journal cannot derive. Declaring any of
// it writes no posting, so none of it is a movement of money.
//
// availableFunds is the TOTAL spendable right now, unused credit included —
// never the reader's own money, and never a statement field merely labelled
// "balance".
export interface AccountState {
  availableFunds?: Money;
  availableFundsAt?: string;
}

// Provenance of a statement's separate effective debt observation. This is
// not an Account field; it belongs to provider/Borrowing read projections.
export type DebtSource = "derived" | "reported" | "rolled_forward" | "journal" | "replay" | "unattributed_account_debt";

// One Borrowing's resolved debt on the account that is its facility. Context
// beside the account's own position, never a replacement for it.
export interface LinkedBorrowingDebt {
  agreementId: string;
  creditor: string;
  debt: Money | null;
  debtSource?: DebtSource;
}

// What an account holds and what it owes, both non-negative and at most one of
// them non-zero. The server resolves which way the account's stored figure
// points; a reader never decides it from a sign or an account type.
export interface PositionAmounts {
  held: Money;
  owed: Money;
}

export interface AccountPosition {
  // In the account's own currency.
  native: PositionAmounts;
  // In the book's base currency; null when no rate could value it (unknown,
  // never zero).
  book: PositionAmounts | null;
  // In the reporting currency; null when unavailable.
  reporting: PositionAmounts | null;
}

export interface AccountBalance {
  account: LedgerAccount;
  // Stored accounting figures in the account's own sign convention, kept for
  // reconciliation and compatibility. Money semantics come from `position`.
  nativeBalance: Money;
  carryingBookValue: Money;
  reportingValue?: Money | null;
  position?: AccountPosition;
  // "borrowing" when a Borrowing facility answers for this account's debt;
  // "unattributed" when it owes and none does yet.
  debtAttribution?: "borrowing" | "unattributed";
  linkedBorrowings?: LinkedBorrowingDebt[];
  state?: AccountState;
}

// What is held minus what is owed. Every account counts once, on the side its
// position puts it; a reported account balance and a credit limit contribute
// nothing.
export interface NetWorth {
  assets: Money;
  debt: Money;
  total: Money;
  currency: Currency;
  reporting?: Money | null;
  reportingAssets?: Money | null;
  reportingDebt?: Money | null;
  // Every contribution to debt, each counted once, with where it came from.
  liabilities?: NetWorthLiability[];
  // False when some amount could not be resolved or valued; it is then left
  // out of the totals rather than counted as zero.
  complete: boolean;
  // Trust, in separate dimensions; none of them removes a known amount.
  valuationComplete?: boolean;
  attributionComplete?: boolean;
  evidenceComplete?: boolean;
}

export interface NetWorthLiability {
  debtSource?: DebtSource;
  agreementId?: string;
  creditor?: string;
  accountId?: string;
  accountName?: string;
  amount: Money | null;
  book: Money | null;
  evidenceComplete?: boolean;
}

export interface Category {
  id: string;
  bookId: string;
  name: string;
}

export interface Book {
  id: string;
  ownerId: string;
  name: string;
  baseCurrency: Currency;
  reportingCurrency?: Currency;
  archived?: boolean;
}

export interface Amounts {
  base: Money;
  reporting?: Money | null;
  reportingComplete: boolean;
}

export interface CategoryTotals {
  categoryId?: string;
  name: string;
  expenses: Amounts;
  income: Amounts;
  count: number;
}

export interface SpendingReport {
  bookId: string;
  from: string;
  to: string;
  baseCurrency: Currency;
  reportingCurrency: Currency;
  expenses: Amounts;
  income: Amounts;
  byCategory: CategoryTotals[];
  transactionCount: number;
  transactions: BudgetTransaction[];
}

export interface Posting {
  id: string;
  transactionId: string;
  bookId: string;
  accountId: string;
  side: "debit" | "credit";
  amount: Money;
  bookValue: Money;
}

export interface JournalEntry {
  planningAssignment?: PlanningAssignment | null;
  transaction: {
    id: string;
    bookId: string;
    effectiveAt: string;
    recordedAt: string;
    description: string;
    revision: number;
    references?: Array<{ kind: "refund_of" | "reversal_of"; transactionId: string }>;
    categoryId?: string;
    externalId?: string;
    note?: string;
    operation?: string;
  };
  postings: Posting[];
  bookValue?: Money;
  reporting?: Money | null;
}

export interface WorkspaceManifest {
  book: { id: string; ownerId: string; name?: string; baseCurrency: Currency };
  // Seeded accounts by purpose. A workspace repaired by hand may name none of
  // them, so every entry is optional.
  accounts: Partial<Record<"primary_asset" | "default_expense" | "default_income" | "fee_expense", LedgerAccount>>;
}

export interface Tracker {
  id: string;
  bookId: string;
  name: string;
  currency: Currency;
  accountIds: string[];
}

export interface Budget {
  id: string;
  bookId: string;
  trackerId: string;
  limit: Money;
  startsAt: string;
  endsAt: string;
}

// A tracker with the target that applies to the requested moment (if any)
// and how much of it is used.
export interface TrackerView {
  tracker: Tracker;
  budget?: Budget | null;
  summary?: BudgetSummary | null;
}

// Soft delete: the account is archived, never removed.
export interface AccountRetirement {
  account: LedgerAccount;
  archived: boolean;
}

export interface BudgetSummary {
  budgetId: string;
  currency: Currency;
  limitMinor: number;
  consumedMinor: number;
  releasedMinor: number;
  spentMinor: number;
  incomeMinor: number;
  remainingMinor: number | null;
  overBudgetMinor: number | null;
  isOverBudget: boolean | null;
  provisionalRemainingMinor: number;
  complete: boolean;
  unlinkedSources: number;
}

export interface UserPreferences {
  defaultCurrency: Currency;
}

export interface WorkspaceIDs {
  user: string;
  book: string;
  baseCurrency: Currency;
  cash: string;
  expense: string;
  income: string;
  fees: string;
  period: { key: string; start: string; end: string };
}

export interface AccountStatement {
  account: LedgerAccount;
  from: string;
  to: string;
  // What the running number measures, and where it comes from. The fields are
  // called openingBalance and runningBalance whichever account they describe;
  // on a card those numbers are a debt, and they are the journal's alone.
  figure: "balance" | "debt";
  figureSource: "journal";
  // What the account owes now, which is a different fact from what its
  // journal says: a card tracked by what the bank reports has no postings at
  // all. Absent on an asset, and on a liability nobody has reported.
  effective?: { debt: Money; source: DebtSource };
  openingBalance: Money;
  openingCarryingBookValue: Money;
  movements: Array<{
    transaction: JournalEntry["transaction"];
    balanceDelta: Money;
    carryingBookValueDelta: Money;
    runningBalance: Money;
    runningBookValue: Money;
  }>;
  closingBalance: Money;
  closingCarryingBookValue: Money;
}

export interface MoneyFlowReport {
  knownExternalInflow: Money;
  knownExternalOutflow: Money;
  internalTransferBookValue: Money;
  nonFlowAdjustments: Money;
  unclassifiedBalanceDelta: Money;
  netChange: Money;
  classificationComplete: boolean;
  ambiguousTransactionIds: string[];
}

export interface DashboardData {
  ids: WorkspaceIDs;
  book: Book;
  accounts: LedgerAccount[];
  balances: AccountBalance[];
  netWorth: NetWorth;
  entries: JournalEntry[];
  moneyFlow: MoneyFlowReport | null;
  categories: Category[];
  report: SpendingReport;
}

// ---- P1 planning (ADR-0019/0020) ----
export type PlanKind = "income" | "expense";
export type PlanStatus = "cancelled" | "planned" | "partially_fulfilled" | "fulfilled" | "overdue";
export type ForecastMode = "none" | "remaining";

export interface PlanItem {
  id: string;
  bookId: string;
  kind: PlanKind;
  name: string;
  description?: string;
  categoryId?: string;
  amount: Money;
  periodStart: string;
  periodEnd: string;
  dueDate?: string | null;
  cancelledAt?: string | null;
  note?: string;
  externalId?: string;
  groupId?: string;
}

// A plan group composes plan items and carries no amount of its own: its
// planned value is the sum of its children.
export interface PlanGroup {
  id: string;
  bookId: string;
  name: string;
  note?: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  dueDate?: string | null;
  externalId?: string;
  archivedAt?: string | null;
}

// One financial fact owned by a planning target: a journal transaction or an
// account movement, exactly one of the two ids. There is no separate planning
// amount to disagree with the fact's own.
export interface AssignedTransaction {
  transactionId?: string;
  accountMovementId?: string;
  description?: string;
  effectiveAt: string;
  amount: Money;
  bookValue: Money;
}

export type AggregationStatus = "complete" | "incomplete";

export interface PlanGroupView {
  group: PlanGroup;
  children: PlanItemView[];
  plannedByCurrency: Money[];
  plannedBase: Money;
  childActualBase: Money;
  directActualBase: Money;
  totalActualBase: Money;
  remainingBase: Money;
  reportingCurrency: Currency;
  plannedReporting?: Money | null;
  childActualReporting?: Money | null;
  directActualReporting?: Money | null;
  totalActualReporting?: Money | null;
  remainingReporting?: Money | null;
  directAssignments: AssignedTransaction[];
  currencies: Currency[];
  aggregationStatus: AggregationStatus;
}

// The stored assignment, as the write endpoints return it.
export interface PlanAssignment {
  id: string;
  bookId: string;
  // Exactly one subject: a journal transaction or an account movement.
  transactionId?: string;
  accountMovementId?: string;
  planItemId?: string;
  planGroupId?: string;
}

// A transaction has at most one planning owner. Never a list.
export interface PlanningAssignment {
  type: "item" | "group";
  id: string;
  name: string;
}

export interface PlanItemView {
  item: PlanItem;
  status: PlanStatus;
  allocated: Money;
  remaining: Money;
  overrun: Money;
  over: boolean;
  assignments: AssignedTransaction[];
  plannedBase?: Money | null;
  plannedReporting?: Money | null;
  allocatedBase?: Money | null;
  allocatedReporting?: Money | null;
}

export interface PlanTotals {
  plannedIncome: Money;
  plannedExpenses: Money;
  allocatedIncome: Money;
  allocatedExpenses: Money;
  varianceIncome: Money;
  varianceExpenses: Money;
  ratesComplete: boolean;
  reportingCurrency: Currency;
  plannedIncomeReporting: Money;
  plannedExpensesReporting: Money;
  allocatedIncomeReporting: Money;
  allocatedExpensesReporting: Money;
  reportingComplete: boolean;
}

export interface PlanVsActual {
  bookId: string;
  from: string;
  to: string;
  asOf: string;
  currency: Currency;
  items: PlanItemView[];
  totals: PlanTotals;
}

// A specific budget limits one named thing, so what a plan item or group
// already owns is not its business. A general budget is one ceiling over its
// categories and the plan items inside it belong to it.
export type BudgetScope = "specific" | "general";

export interface BudgetRecurrence {
  frequency: "day" | "week" | "month" | "year";
  interval: number;
  timeZone: string;
  count?: number;
  until?: string;
}

export interface BudgetSeries {
  id: string;
  bookId: string;
  anchor: string;
  recurrence: BudgetRecurrence;
  template: PlanningBudget;
  stoppedAt?: string;
}

export interface PlanningBudget {
  id: string;
  bookId: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  limit: Money;
  categoryIds: string[];
  forecastMode: ForecastMode;
  scope: BudgetScope;
  note?: string;
  externalId?: string;
  archivedAt?: string | null;
  seriesId?: string;
  timeZone?: string;
}

// One fact behind a budget's spend. Excluded ones stay listed and marked.
export interface BudgetTransaction {
  transactionId: string;
  description?: string;
  effectiveAt: string;
  bookValue: Money;
  excluded: boolean;
  exclusionNote?: string;
  membership?: "default" | "included" | "excluded";
  // The plan item or group that already accounts for this transaction. A
  // budget never counts one: the plan owns it.
  planned?: PlanningAssignment;
}

export interface BudgetProgressView {
  budget: PlanningBudget;
  spent: Money;
  remaining: Money;
  percentUsed: number;
  over: boolean;
  transactionCount: number;
  transactions: BudgetTransaction[];
  ratesComplete: boolean;
  spentBase: Money;
  spentReporting?: Money | null;
  limitBase?: Money | null;
  limitReporting?: Money | null;
  remainingBase?: Money | null;
  remainingReporting?: Money | null;
  reportingComplete: boolean;
}

export interface BudgetProgress {
  bookId: string;
  from: string;
  to: string;
  asOf: string;
  budgets: BudgetProgressView[];
}

export interface MonthlyCompleteness {
  bookId: string;
  from: string;
  to: string;
  sourceCoverage: { complete: boolean; gaps: string[] };
  reconciliation: { status: "reconciled" | "mismatch" | "unknown"; reason?: string };
  economicMeaning: { confirmedValue: Money; unresolvedValue: Money; unresolvedFacts: string[] };
  valuation: { valuedValue: Money; unvaluedFacts: string[]; unvaluedNativeAmountsByCurrency: Record<string, number> };
  planning: { assignedValue: Money; unassignedValue: Money; unassignedFacts: string[] };
}

export interface MonthlyPlanningSummary {
  bookId: string;
  from: string;
  to: string;
  asOf: string;
  currency: Currency;
  plan: PlanTotals;
  actualIncome: Money;
  actualExpenses: Money;
  budgetCount: number;
  budgetLimit: Money;
  budgetSpent: Money;
  budgetRemaining: Money;
  itemsByStatus: Partial<Record<PlanStatus, number>>;
  daysElapsed: number;
  daysRemaining: number;
  ratesComplete: boolean;
  reportingCurrency: Currency;
  actualIncomeReporting?: Money | null;
  actualExpensesReporting?: Money | null;
  budgetLimitReporting?: Money | null;
  budgetSpentReporting?: Money | null;
  budgetRemainingReporting?: Money | null;
  reportingComplete: boolean;
}

export interface DailyBudgetView {
  budgetId: string;
  name: string;
  remaining: Money;
  daysRemaining: number;
  dailyAllowance: Money;
  remainingBase?: Money | null;
  dailyAllowanceBase?: Money | null;
  remainingReporting?: Money | null;
  dailyAllowanceReporting?: Money | null;
}

export interface DailyBudget {
  bookId: string;
  asOf: string;
  currency: Currency;
  budgets: DailyBudgetView[];
  totalRemaining: Money;
  totalDailyAllowance: Money;
  ratesComplete: boolean;
  reportingCurrency: Currency;
  totalRemainingReporting?: Money | null;
  totalDailyAllowanceReporting?: Money | null;
  reportingComplete: boolean;
}

export interface Forecast {
  bookId: string;
  asOf: string;
  until: string;
  currency: Currency;
  ownedAssets: Money;
  remainingPlannedIncome: Money;
  remainingPlannedExpenses: Money;
  remainingBudgets: Money;
  projected: Money;
  ratesComplete: boolean;
  reportingCurrency: Currency;
  ownedAssetsReporting?: Money | null;
  remainingPlannedIncomeReporting?: Money | null;
  remainingPlannedExpensesReporting?: Money | null;
  remainingBudgetsReporting?: Money | null;
  projectedReporting?: Money | null;
  reportingComplete: boolean;
}

export interface PlanningFxRate {
  bookId: string;
  baseCurrency: Currency;
  quoteCurrency: Currency;
  rate: string;
  effectiveFrom?: string | null;
  note?: string;
  updatedAt: string;
}
